import { Controller, useFormContext } from 'react-hook-form'
import {
  CurecallInput,
  CurecallSelect,
  CurecallInputLabel,
  CurecallInputError,
  CurecallTimePicker,
  CurecallSelectItem,
} from '@honestica/curecall-ui'
import { useState, useId, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  DAY_MULTIPLICATOR,
  HOUR_MULTIPLICATOR,
  MINUTE_MULTIPLICATOR,
  TDelayMultiplicator,
  MULTIPLICATOR_TO_ISO_UNIT,
} from './programmed-message.type'
import { getDateValuesFromSchedule } from './programmed-message.helper'

type TDelayMultiplicatorOption = CurecallSelectItem<TDelayMultiplicator>

type TBeforeOrAfter = 'after' | 'before'
type TBeforeOrAfterOption = CurecallSelectItem<TBeforeOrAfter>

export default function ScheduleFields() {
  const { t } = useTranslation()
  const {
    setValue,
    control,
    formState: { errors, defaultValues },
    clearErrors,
  } = useFormContext()
  const DELAY_MULTIPLICATOR_OPTIONS: TDelayMultiplicatorOption[] = [
    { label: t('pages.configuration.messages.minutes'), value: MINUTE_MULTIPLICATOR },
    { label: t('pages.configuration.messages.hours'), value: HOUR_MULTIPLICATOR },
    { label: t('pages.configuration.messages.days'), value: DAY_MULTIPLICATOR },
  ]
  const BEFORE_OR_AFTER_OPTIONS: TBeforeOrAfterOption[] = [
    { label: t('pages.configuration.messages.after'), value: 'after' },
    { label: t('pages.configuration.messages.before'), value: 'before' },
  ]
  const FIELD_NAME = 'schedule'

  const id = useId()
  const [delayValue, setDelayValue] = useState<number>(0)
  const [delayMultiplicator, setDelayMultiplicator] = useState<TDelayMultiplicatorOption>()
  const [beforeOrAfter, setBeforeOrAfter] = useState<TBeforeOrAfterOption>()

  useEffect(() => {
    if (defaultValues?.schedule) {
      const { isBefore, value, multiplicator } = getDateValuesFromSchedule(defaultValues.schedule)
      const beforeOrAfter = isBefore ? 'before' : 'after'
      setBeforeOrAfter(BEFORE_OR_AFTER_OPTIONS.find((option) => option.value === beforeOrAfter))
      setDelayMultiplicator(DELAY_MULTIPLICATOR_OPTIONS.find((option) => option.value === multiplicator))
      setDelayValue(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues?.schedule?.durationDiff])

  const updateDurationDiff = ({
    value,
    multiplicator,
    moment,
  }: {
    value?: number
    multiplicator?: number
    moment?: TBeforeOrAfter
  }) => {
    clearErrors(FIELD_NAME)
    if (value !== undefined && multiplicator && moment) {
      const isoUnit = MULTIPLICATOR_TO_ISO_UNIT[multiplicator as TDelayMultiplicator]
      const sign = moment === 'before' ? '-' : '+'
      const timeDesignator = multiplicator === DAY_MULTIPLICATOR ? '' : 'T'
      const durationDiff = `${sign}P${timeDesignator}${value}${isoUnit}`
      setValue(`${FIELD_NAME}.durationDiff`, durationDiff)
    }
  }

  const handleChangeDelayValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value)
    setDelayValue(value)
    updateDurationDiff({
      value,
      multiplicator: delayMultiplicator?.value,
      moment: beforeOrAfter?.value,
    })
  }
  const handleChangeDelayMultiplicator = (choosenOption: TDelayMultiplicatorOption) => {
    setDelayMultiplicator(choosenOption)
    updateDurationDiff({
      value: delayValue,
      multiplicator: choosenOption?.value,
      moment: beforeOrAfter?.value,
    })
    if (choosenOption.value !== DAY_MULTIPLICATOR) {
      setValue(`${FIELD_NAME}.atTime`, undefined)
    }
  }
  const handleChangeBeforeOrAfter = (choosenOption: TBeforeOrAfterOption) => {
    setBeforeOrAfter(choosenOption)
    updateDurationDiff({
      value: delayValue,
      multiplicator: delayMultiplicator?.value,
      moment: choosenOption.value,
    })
  }
  const handleOnChangeTime = (newTime: string | null) => {
    setValue(`${FIELD_NAME}.atTime`, newTime || undefined)
  }

  return (
    <Controller
      control={control}
      name="schedule.durationDiff"
      rules={{ required: true }}
      render={() => (
        <fieldset id={id} className="c-flex c-flex-wrap">
          <div>
            <CurecallInputLabel
              htmlFor={id}
              label={t('pages.configuration.messages.message-programmation')}
              isRequired={true}
              tooltipContent={t('pages.configuration.messages.message-programmation-tooltip')}
              tooltipPosition="right"
            />
            <div className="c-flex c-gap-4">
              <CurecallInput
                aria-label="delay-value"
                placeholder="0"
                isRequired={true}
                onChange={handleChangeDelayValue}
                value={delayValue}
                type="number"
                min="0"
                wrapperClassName="c-w-20"
                errorMessage={errors?.[FIELD_NAME] && ' '}
              />

              <CurecallSelect
                aria-label="delay-multiplicator"
                items={DELAY_MULTIPLICATOR_OPTIONS}
                onChange={handleChangeDelayMultiplicator}
                selectedItem={delayMultiplicator}
                placeholder="Minute(s)"
                isRequired={true}
                wrapperClassName="c-w-36"
                errorMessage={errors?.[FIELD_NAME] && ' '}
              />

              <CurecallSelect
                aria-label="before-or-after"
                items={BEFORE_OR_AFTER_OPTIONS}
                onChange={handleChangeBeforeOrAfter}
                selectedItem={beforeOrAfter}
                placeholder={t('pages.configuration.messages.after')}
                isRequired={true}
                wrapperClassName="c-w-36"
                errorMessage={errors?.[FIELD_NAME] && ' '}
              />
            </div>

            {errors?.[FIELD_NAME] && (
              <CurecallInputError
                errorMessage={t('pages.configuration.messages.thanks-to-fill-sending-date')}
              />
            )}
          </div>

          {delayMultiplicator?.value === DAY_MULTIPLICATOR && (
            <div className="c-ml-20">
              <CurecallTimePicker
                label={t('pages.configuration.messages.send-at')}
                defaultValue={defaultValues?.schedule?.atTime}
                clearButtonLabel={t('pages.configuration.messages.clear-hour-field')}
                onChangeTime={handleOnChangeTime}
                tooltipContent={t('pages.configuration.messages.send-at-tooltip')}
                tooltipPosition="right"
              />
            </div>
          )}

          {beforeOrAfter?.value === 'before' && (
            <p className="c-mt-8 c-mb-0 c-font-nexa c-text-grey-600 c-w-full">
              <Trans
                i18nKey="pages.configuration.messages.before-disclaimer"
                values={{
                  delayValue,
                  delayLabel: (delayMultiplicator?.label as string)?.toLocaleLowerCase(),
                }}
                components={{ u: <u /> }}
              />
            </p>
          )}
        </fieldset>
      )}
    />
  )
}
